import { useEffect, useMemo, useState } from 'react';
import { Empty, Form, Image, Row, Col, Tag, Typography } from 'antd';
import { isEmpty } from 'lodash';
import { authdictionaryApi } from '@services/Conference/sign';
import { cardTypes, expertLevels } from '@utils/constants';
import { buildAuthDictMaps, formatArea, formatOfficeAndPost, getExpertUpdateTrace } from '@/utils/utils';
import { canViewExpertMobileFull, maskExpertMobileDisplay } from '@/utils/expertMobileVisibility';
import WorkExperiences from '@/components/common/WorkExperiences';

const { Paragraph, Text } = Typography;

const formItemLayout = {
    labelCol: { sm: { span: 24 }, md: { span: 6 }, lg: { span: 4 } },
    wrapperCol: { sm: { span: 24 }, md: { span: 18 }, lg: { span: 20 } },
};

const EMPTY = <Text type="secondary">暂无</Text>;

const getLabelByValue = (list, value, valueKey = 'value', labelKey = 'label') => list.find(item => item[valueKey] === value)?.[labelKey];

/** 主手机号展示：与编辑页一致，100 开头视为无手机号；无查看权限时脱敏 */
const renderMainMobile = (memMobile, canViewFull) => {
    if (!memMobile || String(memMobile).startsWith('100')) {
        return <Tag color="red">无手机号</Tag>;
    }
    if (!canViewFull) {
        return maskExpertMobileDisplay(memMobile);
    }
    return memMobile;
};

/** 其他手机号：空为暂无；无查看权限时脱敏 */
const renderOtherMobile = (otherMobile, canViewFull) => {
    if (otherMobile === null || otherMobile === undefined || otherMobile === '') {
        return EMPTY;
    }
    if (!canViewFull) {
        return maskExpertMobileDisplay(otherMobile);
    }
    return otherMobile;
};

/** 性别：无数据时展示「未填写」（不用「暂无」） */
const renderGenderDisplay = memGender => {
    if (memGender === 1) {
        return '男';
    }
    if (memGender === 0) {
        return '女';
    }
    return <Text type="secondary">未填写</Text>;
};

/** 标量与字符串：空则展示与机构详情一致的占位 */
const asText = value => {
    if (value === 0) {
        return '0';
    }
    if (Array.isArray(value)) {
        const s = value.filter(Boolean).join('、');
        return s || EMPTY;
    }
    if (value === null || value === undefined || value === '') {
        return EMPTY;
    }
    return value;
};

const sanitizeHtmlToText = html => {
    if (!html) {
        return '';
    }

    return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
};

const renderIntroduction = introduction => {
    const text = sanitizeHtmlToText(introduction);

    if (!text) {
        return EMPTY;
    }

    return <Paragraph className="expert-profile-view__paragraph">{text}</Paragraph>;
};

/**
 * 与专家列表一致：优先使用接口扁平结构 ssoMemberWorkExperiencesEntities；
 * 否则将表单用的嵌套 experiences（含 childs）展平为同类行数据。
 */
const flattenNestedExperiences = experiences => {
    if (!Array.isArray(experiences) || !experiences.length) {
        return [];
    }
    const rows = [];
    experiences.forEach(exp => {
        const unitName = exp.unitName || '';
        const memUnitId = exp.memUnitId;
        const organisation = exp.organisation;
        const childs = exp.childs || [];
        if (!childs.length) {
            rows.push({
                id: exp.id,
                unitName,
                memUnitId,
                organisation,
                unitOffice: exp.unitOffice,
                unitPost: exp.unitPost,
                memOfficeCode: exp.memOfficeCode,
                memPostCode: exp.memPostCode,
            });
            return;
        }
        childs.forEach((child, idx) => {
            rows.push({
                id: child.id ?? `${exp.id}_${idx}`,
                unitName,
                memUnitId,
                organisation,
                unitOffice: child.unitOffice,
                unitPost: child.unitPost,
                unitJobTitle: child.unitJobTitle,
                memOfficeCode: child.memOfficeCode,
                memPostCode: child.memPostCode,
            });
        });
    });
    return rows;
};

const getWorkExperienceRowsForDisplay = expert => {
    if (!expert) {
        return [];
    }
    const flat = expert.ssoMemberWorkExperiencesEntities;
    if (Array.isArray(flat) && flat.length > 0) {
        return flat;
    }
    return flattenNestedExperiences(expert.experiences);
};

const renderContacts = contacts => {
    if (!Array.isArray(contacts) || !contacts.length) {
        return EMPTY;
    }

    return (
        <div className="expert-profile-view__contact-list">
            {contacts.map((contact, index) => (
                <div key={contact.id || `${contact.name || 'contact'}_${index}`} className="expert-profile-view__contact-item">
                    <div>{contact.name || '未填写姓名'}</div>
                    <div>{asText(contact.phone)}</div>
                    <div>{asText(contact.email)}</div>
                </div>
            ))}
        </div>
    );
};

export default function ExpertProfileView({ expert }) {
    const [authDict, setAuthDict] = useState([]);

    useEffect(() => {
        authdictionaryApi().then(res => {
            if (!isEmpty(res?.options) && Array.isArray(res.options)) {
                setAuthDict(res.options);
            }
        });
    }, []);

    const dictMaps = useMemo(() => buildAuthDictMaps(authDict), [authDict]);
    const workExperienceRows = useMemo(() => getWorkExperienceRowsForDisplay(expert), [expert]);
    const phoneUpdateTrace = useMemo(() => getExpertUpdateTrace(expert, 'phone'), [expert]);
    const canViewMobileFull = useMemo(() => canViewExpertMobileFull(expert?.memMobile), [expert]);
    const introUpdateTrace = useMemo(() => getExpertUpdateTrace(expert, 'intro'), [expert]);
    const unitUpdateTrace = useMemo(() => getExpertUpdateTrace(expert, 'unit'), [expert]);
    const laborfeeUpdateTrace = useMemo(() => getExpertUpdateTrace(expert, 'laborfee'), [expert]);

    if (!expert) {
        return (
            <div className="expert-profile-view expert-profile-view--empty">
                <Empty description="暂无专家信息" />
            </div>
        );
    }

    const cardTypeLabel = getLabelByValue(cardTypes, expert.idcardType, 'code', 'name');
    const isIdCardType = Number(expert.idcardType) === 1;
    const levelVal = expert.expertLevel;
    const expertLevelLabel = expertLevels.find(l => l.level === levelVal || (levelVal != null && levelVal !== '' && l.level === Number(levelVal)))?.label;
    const honoraryTitles = Array.isArray(expert.honoraryTitles)
        ? expert.honoraryTitles
        : typeof expert.honoraryTitles === 'string'
          ? (() => {
                try {
                    const parsed = JSON.parse(expert.honoraryTitles);
                    return Array.isArray(parsed) ? parsed : [expert.honoraryTitles].filter(Boolean);
                } catch (_error) {
                    return expert.honoraryTitles.split(/[、,，;；\n]+/).filter(Boolean);
                }
            })()
          : [];

    // 所在地
    const locationText = formatArea(expert);

    return (
        <div className="expert-profile-view">
            <Row justify="center" className="expert-profile-view__row">
                <Col flex="0 1 1200px" className="expert-profile-view__col">
                    <Form {...formItemLayout} className="expert-profile-view__form" layout="horizontal">
                        <section id="section-basic" className="expert-profile-view__section">
                            <div className="expert-profile-view__module-title">基本信息</div>

                            <Form.Item label="姓名">{asText(expert.memUsername)}</Form.Item>
                            <Form.Item label="手机号">
                                <span className="expert-profile-view__mobile-inline">
                                    <span className="expert-profile-view__mobile-primary">{renderMainMobile(expert.memMobile, canViewMobileFull)}</span>
                                    {phoneUpdateTrace && (
                                        <span className="expert-phone-update-trace">
                                            <i className="bi bi-clock" />
                                            <span>
                                                最后更新：{phoneUpdateTrace.updatedAt}
                                                {phoneUpdateTrace.updatedByLabel ? ` ${phoneUpdateTrace.updatedByLabel}` : ''}
                                            </span>
                                        </span>
                                    )}
                                </span>
                            </Form.Item>
                            <Form.Item label="其他手机号">{renderOtherMobile(expert.otherMobile, canViewMobileFull)}</Form.Item>
                            <Form.Item label="专家级别">{asText(expertLevelLabel)}</Form.Item>
                            <Form.Item label="单位任职">
                                <div className="expert-profile-view__unit-block">
                                    <div className="expert-profile-view__unit-row">
                                        <div className="expert-profile-view__unit-main">
                                            <WorkExperiences
                                                workExperiences={workExperienceRows}
                                                showSerialNumber={false}
                                                customFormatter={(item, context) => {
                                                    const officePostDisplay = formatOfficeAndPost(item, context);
                                                    const unitName = item.unitName || '';
                                                    return {
                                                        unitName,
                                                        officePostDisplay,
                                                    };
                                                }}
                                                formatterContext={dictMaps}
                                            />
                                        </div>
                                        {unitUpdateTrace && (
                                            <div className="expert-phone-update-trace expert-profile-view__unit-trace">
                                                <i className="bi bi-clock" />
                                                <span>
                                                    最后更新：{unitUpdateTrace.updatedAt}
                                                    {unitUpdateTrace.updatedByLabel ? ` ${unitUpdateTrace.updatedByLabel}` : ''}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Form.Item>
                            <Form.Item label="职称">{asText(expert.memJobtitle)}</Form.Item>
                            <Form.Item label="性别">{renderGenderDisplay(expert.memGender)}</Form.Item>
                            <Form.Item label="个人介绍">
                                <div className="expert-profile-view__intro-block">
                                    {renderIntroduction(expert.memIntroduction)}
                                    {introUpdateTrace && (
                                        <div className="expert-phone-update-trace expert-profile-view__intro-trace">
                                            <i className="bi bi-clock" />
                                            <span>
                                                最后更新：{introUpdateTrace.updatedAt}
                                                {introUpdateTrace.updatedByLabel ? ` ${introUpdateTrace.updatedByLabel}` : ''}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </Form.Item>
                            <Form.Item label="简介附件">
                                {expert.introFile ? (
                                    <a href={expert.introFile} target="_blank" rel="noreferrer">
                                        {expert.introFileName || '查看附件'}
                                    </a>
                                ) : (
                                    EMPTY
                                )}
                            </Form.Item>
                            <Form.Item label="个人头像">
                                {expert.expertAvatar ? (
                                    <div
                                        style={{
                                            width: 100,
                                            height: 100,
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            display: 'inline-block',
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        <Image src={expert.expertAvatar} width={100} height={100} style={{ objectFit: 'cover', display: 'block' }} alt="专家头像" />
                                    </div>
                                ) : (
                                    EMPTY
                                )}
                            </Form.Item>
                            <Form.Item label="宣传照片">{expert.publicityPhoto ? <Image src={expert.publicityPhoto} width={180} alt="专家宣传照片" /> : EMPTY}</Form.Item>
                        </section>

                        <section id="section-pro" className="expert-profile-view__section">
                            <div className="expert-profile-view__module-title">专业信息</div>
                            <Form.Item label="学术任职">{asText(expert.academicPosition)}</Form.Item>
                            <Form.Item label="研究成果">{asText(expert.researchAchievements)}</Form.Item>
                            <Form.Item label="管理专长">{asText(expert.managementExpertises)}</Form.Item>
                            <Form.Item label="医学专长">{asText(expert.medicalExpertises)}</Form.Item>
                            <Form.Item label="荣誉头衔">{asText(honoraryTitles)}</Form.Item>
                        </section>

                        <section id="section-payment" className="expert-profile-view__section">
                            <div className="expert-profile-view__module-title expert-profile-view__module-title--with-trace">
                                <span>收款信息</span>
                                {laborfeeUpdateTrace && (
                                    <span className="expert-phone-update-trace expert-profile-view__payment-trace">
                                        <i className="bi bi-clock" />
                                        <span>
                                            最后更新：{laborfeeUpdateTrace.updatedAt}
                                            {laborfeeUpdateTrace.updatedByLabel ? ` ${laborfeeUpdateTrace.updatedByLabel}` : ''}
                                        </span>
                                    </span>
                                )}
                            </div>
                            <Form.Item label="证件类型">{asText(cardTypeLabel)}</Form.Item>
                            <Form.Item label="证件号">{asText(expert.idCard)}</Form.Item>
                            <Form.Item label="出生日期">{asText(expert.birthday)}</Form.Item>
                            {!isIdCardType && (
                                <>
                                    <Form.Item label="国籍">{asText(expert.nationality)}</Form.Item>
                                    <Form.Item label="出生城市">{asText(expert.birthPlace)}</Form.Item>
                                </>
                            )}
                            <Form.Item label="银行账号类型">{asText(expert.bankType === 2 ? '境外银行账户' : '境内银行账户')}</Form.Item>
                            {expert.bankType === 2 ? (
                                <>
                                    <Form.Item label="收款人名称（英文）">{asText(expert.payeeUsername)}</Form.Item>
                                    <Form.Item label="收款人银行预留电话">{asText(expert.payeePhone)}</Form.Item>
                                    <Form.Item label="收款人境外汇款住址（英文）">{asText(expert.payeeAddress)}</Form.Item>
                                    <Form.Item label="开户银行名称（英文）">{asText(expert.bankName)}</Form.Item>
                                    <Form.Item label="银行账号">{asText(expert.bankCardNo)}</Form.Item>
                                    <Form.Item label="SWIFT/BIC Code">{asText(expert.swiftBicCode)}</Form.Item>
                                </>
                            ) : (
                                <>
                                    <Form.Item label="开户银行">{asText(expert.bankName)}</Form.Item>
                                    <Form.Item label="银行账号">{asText(expert.bankCardNo)}</Form.Item>
                                </>
                            )}
                        </section>

                        <section id="section-more" className="expert-profile-view__section">
                            <div className="expert-profile-view__module-title">更多信息</div>
                            <Form.Item label="微信号">{asText(expert.expertWechat)}</Form.Item>
                            <Form.Item label="邮箱">{asText(expert.expertEmail)}</Form.Item>
                            <Form.Item label="所在地">{locationText ? locationText : EMPTY}</Form.Item>
                            <Form.Item label="民族">{asText(expert.national)}</Form.Item>
                            <Form.Item label="毕业院校">{asText(expert.school)}</Form.Item>
                            <Form.Item label="最高学历">{asText(expert.education)}</Form.Item>
                            <Form.Item label="工作年限">{asText(expert.workingYears)}</Form.Item>
                            <Form.Item label="饮食习惯">{asText(expert.eatingHabits)}</Form.Item>
                            <Form.Item label="爱好">{asText(expert.hobby)}</Form.Item>
                            <Form.Item label="相关联系人">{renderContacts(expert.contacts)}</Form.Item>
                            <Form.Item label="备注">{expert.remarks ? <Paragraph className="expert-profile-view__paragraph">{expert.remarks}</Paragraph> : EMPTY}</Form.Item>
                        </section>
                    </Form>
                </Col>
            </Row>
        </div>
    );
}
